import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import { range } from '../helpers';
import { isDaysInDateRangeBounds } from '../services/DateRange';
import {
  addDays,
  addMonths,
  addWeeks,
  type Datelike,
  formatDatelike,
  getEndOfMonth,
  getStartOfMonth,
  getStartOfWeek,
  getWeekday,
  isBetweenDaysInclusiveLeft,
  isSameDay,
  isSameMonth,
  isToday,
  preserveTime,
  subtractMonths,
  toDate,
  weeksBetween,
} from '../services/DateTime';

import T from './Translate';

type Props = {
  date?: Datelike;
  min?: Datelike;
  max?: Datelike;
  title?: string;
  className?: string;
  dateClassName?(date: Date): string;
  weekdayClassName?(weekday: number): string;
  disabledWeekdays?: number[];
  disabledDates?: Datelike[];
  enabledDates?: Datelike[];
  onDateClick(date: Datelike): void;
  onWeekdayClick?: (weekday: number) => void;
};

interface CalendarWeekdaysProps {
  active: Datelike;

  weekdayClassName?(weekday: number): string;

  onWeekdayClick?(weekday: number): void;
}

export function CalendarWeekdays(props: CalendarWeekdaysProps) {
  const firstDayOfWeek = useMemo(() => getStartOfWeek(props.active), [props.active]);

  return (
    <div className="calendar_v2__weekdays">
      {range(0, 7).map(weekday => {
        const classes = ['calendar_v2__weekday'];
        if (props.weekdayClassName) {
          classes.push(props.weekdayClassName(weekday));
        }
        return (
          <button
            className={classes.join(' ')}
            key={`header-day-${weekday}`}
            onClick={e => {
              e.stopPropagation();
              props.onWeekdayClick?.(weekday);
            }}
            type="button"
          >
            {formatDatelike(addDays(firstDayOfWeek, weekday), 'eeeee')}
          </button>
        );
      })}
    </div>
  );
}

interface CalendarTableProp {
  active: Date;

  isEnabled?(day: Datelike): boolean;

  dateClassName?(day: Datelike): string;

  onDateClick(day: Datelike): void;
}

export function CalendarTable(props: CalendarTableProp) {
  const firstDateOfMonth = useMemo(() => getStartOfMonth(props.active), [props.active]);
  const firstDateOfCalendar = useMemo(() => getStartOfWeek(firstDateOfMonth), [firstDateOfMonth]);
  const numOfWeeks = useMemo(() => weeksBetween(firstDateOfMonth, getEndOfMonth(props.active)), [props.active]);

  return (
    <div className="calendar_v2__body">
      {range(0, numOfWeeks).map(week => (
        <div className="calendar_v2__row" key={`week-${week}`}>
          {range(0, 7).map(weekday => {
            const day = addDays(addWeeks(firstDateOfCalendar, week), weekday);
            const classes = ['calendar_v2__day'];
            if (!isSameMonth(day, props.active)) {
              classes.push('different-month');
            }
            if (isToday(day)) {
              classes.push('today');
            }
            if (props.dateClassName) {
              classes.push(props.dateClassName(day));
            }

            return (
              <button
                className={classes.join(' ')}
                disabled={props.isEnabled ? !props.isEnabled(day) : false}
                key={formatDatelike(day, `'day-'d-M`)}
                onClick={e => {
                  e.stopPropagation();
                  props.onDateClick(preserveTime(day, props.active));
                }}
                type="button"
              >
                {formatDatelike(day, 'd')}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function Calendar(props: Props) {
  const [active, setActive] = useState(toDate(props.date));
  const previousMonth = useMemo(() => getEndOfMonth(subtractMonths(active, 1)), [active]);
  const nextMonth = useMemo(() => getStartOfMonth(addMonths(active, 1)), [active]);

  useEffect(() => {
    setActive(toDate(props.date));
  }, [props.date]);

  const isWeekdayAvailable = useCallback(
    (weekday: number) => !props.disabledWeekdays.some(w => w === weekday),
    [props.disabledWeekdays],
  );

  const isInRange = useCallback(
    (day: Datelike) => isBetweenDaysInclusiveLeft(day, props.min, props.max),
    [props.min, props.max],
  );

  const isEnabled = useCallback(
    (day: Datelike): boolean => {
      const isInBounds = isDaysInDateRangeBounds(day, props.min, props.max);
      if (props.enabledDates.some(d => isSameDay(d, day))) {
        return isInBounds;
      }
      const isDisabledDate = props.disabledDates.some(d => isSameDay(d, day));
      const isDisabledWeekday = !isWeekdayAvailable(getWeekday(day));
      return isInBounds && !isDisabledWeekday && !isDisabledDate;
    },
    [isWeekdayAvailable, props.min, props.max, props.disabledDates],
  );

  return (
    <div className={classNames('calendar_v2', props.className)}>
      {props.title ? (
        <div className="calendar_v2__title">
          <T id={props.title} />
        </div>
      ) : null}
      <div className="calendar_v2__header">
        <button
          className="button button-sm"
          disabled={!isInRange(previousMonth)}
          onClick={e => {
            e.stopPropagation();
            setActive(previousMonth);
          }}
          type="button"
        >
          <i className="icon icon-arrow-right-circle icon-flip-y icon-24" />
        </button>
        <div className="calendar_v2__header__month">{formatDatelike(active, 'LLLL yyyy')}</div>
        <button
          className="button button-sm"
          disabled={!isInRange(nextMonth)}
          onClick={e => {
            e.stopPropagation();
            setActive(nextMonth);
          }}
          type="button"
        >
          <i className="icon icon-arrow-right-circle icon-24" />
        </button>
      </div>
      <CalendarWeekdays
        active={active}
        onWeekdayClick={props.onWeekdayClick}
        weekdayClassName={weekday => {
          if (props.weekdayClassName) return props.weekdayClassName(weekday);
          if (!isWeekdayAvailable(weekday)) return 'unavailable';
          return null;
        }}
      />
      <CalendarTable
        active={active}
        dateClassName={props.dateClassName}
        isEnabled={isEnabled}
        onDateClick={props.onDateClick}
      />
    </div>
  );
}

Calendar.defaultProps = {
  date: new Date(),
  disabledWeekdays: [],
  disabledDates: [],
  enabledDates: [],
} as Partial<Props>;

export default Calendar;
